import { ChevronRight } from "lucide-react";

import { Card, CardBody, CardExtra, CardHead, CardTitle } from "@/components/ui/Card.jsx";
import { TableWrap, Td, Th, Tr } from "@/components/ui/Table.jsx";
import Tag from "@/components/ui/Tag.jsx";
import { EmptyState } from "@/components/ui/Display.jsx";
import { formatDate, formatDuration, formatTime } from "@/lib/format.js";
import { toneOf } from "@/lib/tokens.js";
import { cx } from "@/lib/cx.js";

const scoreTone = (score) => (score >= 70 ? "emerald" : score >= 40 ? "amber" : "rose");

/* ---- Time entries ------------------------------------------------------- */

export function TimeEntriesTable({ rows, total, onOpen }) {
    return (
        <Card>
            <CardHead className="flex-wrap">
                <CardTitle hint="click a row for app-level detail">Time Entries</CardTitle>
                <CardExtra>
                    <Tag tone="emerald" data-entries-total>
                        Total {formatDuration(total)}
                    </Tag>
                </CardExtra>
            </CardHead>

            {rows.length === 0 ? (
                <CardBody>
                    <EmptyState
                        title="No time entries in this range"
                        hint="Entries appear here once the desktop app records a session."
                    />
                </CardBody>
            ) : (
                <TableWrap minWidth="820px">
                    <thead>
                        <tr>
                            <Th className="pl-4.5">Date</Th>
                            <Th>Start Time</Th>
                            <Th>End Time</Th>
                            <Th>Duration</Th>
                            <Th>Project</Th>
                            <Th className="w-56">Productivity</Th>
                            <Th className="w-10 pr-4.5" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <Tr
                                key={r.id}
                                data-entry={r.id}
                                onClick={() => onOpen(r)}
                                className="cursor-pointer"
                            >
                                <Td className="pl-4.5 text-slate-500 dark:text-slate-400">
                                    {formatDate(r.startTime)}
                                </Td>
                                <Td className="font-mono text-[12.5px]">{formatTime(r.startTime)}</Td>
                                <Td className="font-mono text-[12.5px]">
                                    {r.ongoing ? <Tag tone="brand">Ongoing</Tag> : formatTime(r.endTime)}
                                </Td>
                                <Td>
                                    <span className="font-mono text-[12.5px] font-semibold">
                                        {formatDuration(r.duration)}
                                    </span>
                                </Td>
                                <Td className="text-slate-500 dark:text-slate-400">
                                    {r.project ?? <span className="text-slate-400">—</span>}
                                </Td>
                                <Td>
                                    {/* An ongoing entry has no score yet — the
                                        server writes it when the entry ends. A
                                        dash, not 0%: a running session is not
                                        an unproductive one. */}
                                    {r.score == null ? (
                                        <span className="text-slate-400 dark:text-slate-500">—</span>
                                    ) : (
                                        <span className="flex items-center gap-2.5">
                                            <span className="flex h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                                                <span
                                                    className={cx(
                                                        "h-full rounded-full",
                                                        toneOf(scoreTone(r.score)).bar,
                                                    )}
                                                    style={{ width: `${Math.round(r.score)}%` }}
                                                />
                                            </span>
                                            <span
                                                data-score
                                                className="w-11 shrink-0 text-right font-mono text-[12px]"
                                            >
                                                {Math.round(r.score)}%
                                            </span>
                                        </span>
                                    )}
                                </Td>
                                <Td className="pr-4.5 text-right text-slate-300 dark:text-slate-600">
                                    <ChevronRight className="ml-auto size-4" strokeWidth={2.2} />
                                </Td>
                            </Tr>
                        ))}
                    </tbody>
                </TableWrap>
            )}
        </Card>
    );
}

/* ---- Idle times --------------------------------------------------------- */

/*
 * IdleTime stores start, end, duration and which time entry it belongs to.
 * That is the whole model — there is no "reason" and no "included / ignored"
 * status, which is what html-markup's two extra columns showed. They are not
 * here, because a column reading "No activity detected" on every row would look
 * like a measurement and is a constant.
 *
 * The "Session" column IS real, and is the one thing the model knows that the
 * markup did not show: which work session the gap interrupted.
 */
export function IdleTimesTable({ rows, total, onOpen, entryLabels }) {
    return (
        <Card>
            <CardHead className="flex-wrap">
                <CardTitle>Idle Times</CardTitle>
                <CardExtra>
                    <Tag tone="rose" data-idle-total>
                        Total {formatDuration(total)}
                    </Tag>
                </CardExtra>
            </CardHead>

            {rows.length === 0 ? (
                <CardBody>
                    <EmptyState
                        title="No idle periods in this range"
                        hint="An idle period is recorded when there is no keyboard or mouse input while a session is running."
                    />
                </CardBody>
            ) : (
                <TableWrap minWidth="760px">
                    <thead>
                        <tr>
                            <Th className="pl-4.5">Date</Th>
                            <Th>Start Time</Th>
                            <Th>End Time</Th>
                            <Th>Duration</Th>
                            <Th>Session</Th>
                            <Th className="w-28">State</Th>
                            <Th className="w-10 pr-4.5" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <Tr
                                key={r.id}
                                data-idle={r.id}
                                onClick={() => onOpen(r)}
                                className="cursor-pointer"
                            >
                                <Td className="pl-4.5 text-slate-500 dark:text-slate-400">
                                    {formatDate(r.startTime)}
                                </Td>
                                <Td className="font-mono text-[12.5px]">{formatTime(r.startTime)}</Td>
                                <Td className="font-mono text-[12.5px]">
                                    {r.ongoing ? "—" : formatTime(r.endTime)}
                                </Td>
                                <Td>
                                    <span className="font-mono text-[12.5px] font-semibold text-rose-600 dark:text-rose-400">
                                        {r.ongoing ? "—" : formatDuration(r.duration)}
                                    </span>
                                </Td>
                                <Td className="font-mono text-[11.5px] text-slate-400 dark:text-slate-500">
                                    {entryLabels.get(r.timeEntry) ?? "—"}
                                </Td>
                                <Td>
                                    <Tag tone={r.ongoing ? "amber" : "slate"}>
                                        {r.ongoing ? "Open" : "Closed"}
                                    </Tag>
                                </Td>
                                <Td className="pr-4.5 text-right text-slate-300 dark:text-slate-600">
                                    <ChevronRight className="ml-auto size-4" strokeWidth={2.2} />
                                </Td>
                            </Tr>
                        ))}
                    </tbody>
                </TableWrap>
            )}
        </Card>
    );
}
